import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const RegionContext = createContext(null);

// Supported regions with currency and language
export const REGIONS = [
  { code: 'US', name: 'United States', flag: '🇺🇸', currency: 'USD', symbol: '$', language: 'en', langName: 'English' },
  { code: 'IN', name: 'India', flag: '🇮🇳', currency: 'INR', symbol: '₹', language: 'hi', langName: 'हिंदी' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪', currency: 'AED', symbol: 'د.إ', language: 'ar', langName: 'العربية' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', currency: 'GBP', symbol: '£', language: 'en', langName: 'English' },
  { code: 'EU', name: 'Europe', flag: '🇪🇺', currency: 'EUR', symbol: '€', language: 'en', langName: 'English' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', currency: 'SAR', symbol: 'ر.س', language: 'ar', langName: 'العربية' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', currency: 'SGD', symbol: 'S$', language: 'en', langName: 'English' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', currency: 'AUD', symbol: 'A$', language: 'en', langName: 'English' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', currency: 'JPY', symbol: '¥', language: 'ja', langName: '日本語' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', currency: 'EUR', symbol: '€', language: 'de', langName: 'Deutsch' },
];

// Exchange rates (approximate, would be fetched from API in production)
const EXCHANGE_RATES = {
  USD: 1,
  INR: 83.5,
  AED: 3.67,
  GBP: 0.79,
  EUR: 0.92,
  SAR: 3.75,
  SGD: 1.34,
  AUD: 1.53,
  JPY: 154.5,
};

// Translations
export const TRANSLATIONS = {
  en: {
    hero_tagline: 'Telco-Grade Enterprise Security',
    hero_title_1: 'Secure Your',
    hero_title_2: 'Digital Empire',
    hero_desc: 'Complete protection for phones, tablets, IoT devices, and smart city infrastructure. Defend against financial fraud, identity theft, GSM attacks, and network intrusions with AI-powered security.',
    features: 'Features',
    solutions: 'Solutions',
    security: 'Security',
    compliance: 'Compliance',
    about: 'About',
    sign_in: 'Sign In',
    start_trial: 'Start Free Trial',
    watch_demo: 'Watch Demo',
    trusted_by: 'Trusted by 500+ Enterprises',
    mobile_security: 'Mobile Security',
    financial_fraud: 'Financial Fraud',
    identity_protection: 'Identity Protection',
    iot_smart_city: 'IoT/Smart City',
    gsm_telecom: 'GSM/Telecom',
    enterprise: 'Enterprise',
    ai_security: 'AI Security',
    threats_blocked: 'Threats Blocked',
    devices_protected: 'Devices Protected',
    detection_rate: 'Detection Rate',
    response_time: 'Response Time',
    select_region: 'Select Region & Language',
    pricing_after_login: 'Pricing available after sign-in',
  },
  hi: {
    hero_tagline: 'टेल्को-ग्रेड एंटरप्राइज़ सुरक्षा',
    hero_title_1: 'अपने',
    hero_title_2: 'डिजिटल साम्राज्य को सुरक्षित करें',
    hero_desc: 'फ़ोन, टैबलेट, IoT उपकरणों और स्मार्ट सिटी इंफ्रास्ट्रक्चर के लिए संपूर्ण सुरक्षा। AI-संचालित सुरक्षा के साथ वित्तीय धोखाधड़ी, पहचान की चोरी, GSM हमलों से बचाव।',
    features: 'विशेषताएं',
    solutions: 'समाधान',
    security: 'सुरक्षा',
    compliance: 'अनुपालन',
    about: 'हमारे बारे में',
    sign_in: 'साइन इन',
    start_trial: 'निःशुल्क परीक्षण शुरू करें',
    watch_demo: 'डेमो देखें',
    trusted_by: '500+ उद्यमों द्वारा विश्वसनीय',
    mobile_security: 'मोबाइल सुरक्षा',
    financial_fraud: 'वित्तीय धोखाधड़ी',
    identity_protection: 'पहचान सुरक्षा',
    iot_smart_city: 'IoT/स्मार्ट सिटी',
    gsm_telecom: 'GSM/टेलीकॉम',
    enterprise: 'एंटरप्राइज़',
    ai_security: 'AI सुरक्षा',
    threats_blocked: 'खतरे रोके गए',
    devices_protected: 'उपकरण सुरक्षित',
    detection_rate: 'पहचान दर',
    response_time: 'प्रतिक्रिया समय',
    select_region: 'क्षेत्र और भाषा चुनें',
    pricing_after_login: 'साइन-इन के बाद मूल्य निर्धारण उपलब्ध',
  },
  ar: {
    hero_tagline: 'أمان المؤسسات من الدرجة الأولى',
    hero_title_1: 'أمّن',
    hero_title_2: 'إمبراطوريتك الرقمية',
    hero_desc: 'حماية كاملة للهواتف والأجهزة اللوحية وأجهزة إنترنت الأشياء والبنية التحتية للمدينة الذكية. دافع ضد الاحتيال المالي وسرقة الهوية وهجمات GSM.',
    features: 'الميزات',
    solutions: 'الحلول',
    security: 'الأمان',
    compliance: 'الامتثال',
    about: 'من نحن',
    sign_in: 'تسجيل الدخول',
    start_trial: 'ابدأ تجربة مجانية',
    watch_demo: 'شاهد العرض',
    trusted_by: 'موثوق من قبل 500+ مؤسسة',
    mobile_security: 'أمان الجوال',
    financial_fraud: 'الاحتيال المالي',
    identity_protection: 'حماية الهوية',
    iot_smart_city: 'المدينة الذكية',
    gsm_telecom: 'الاتصالات',
    enterprise: 'المؤسسات',
    ai_security: 'أمان الذكاء الاصطناعي',
    threats_blocked: 'التهديدات المحظورة',
    devices_protected: 'الأجهزة المحمية',
    detection_rate: 'معدل الكشف',
    response_time: 'وقت الاستجابة',
    select_region: 'اختر المنطقة واللغة',
    pricing_after_login: 'الأسعار متاحة بعد تسجيل الدخول',
  },
  ja: {
    hero_tagline: '通信事業者グレードのエンタープライズセキュリティ',
    hero_title_1: 'あなたの',
    hero_title_2: 'デジタル帝国を守る',
    hero_desc: '電話、タブレット、IoTデバイス、スマートシティインフラの完全な保護。AI搭載セキュリティで金融詐欺、個人情報盗難、GSM攻撃から防御。',
    features: '機能',
    solutions: 'ソリューション',
    security: 'セキュリティ',
    compliance: 'コンプライアンス',
    about: '会社概要',
    sign_in: 'サインイン',
    start_trial: '無料トライアル開始',
    watch_demo: 'デモを見る',
    trusted_by: '500社以上の企業に信頼',
    mobile_security: 'モバイルセキュリティ',
    financial_fraud: '金融詐欺',
    identity_protection: '個人情報保護',
    iot_smart_city: 'IoT/スマートシティ',
    gsm_telecom: 'GSM/通信',
    enterprise: 'エンタープライズ',
    ai_security: 'AIセキュリティ',
    threats_blocked: 'ブロックされた脅威',
    devices_protected: '保護されたデバイス',
    detection_rate: '検出率',
    response_time: '応答時間',
    select_region: '地域と言語を選択',
    pricing_after_login: 'サインイン後に価格を表示',
  },
  de: {
    hero_tagline: 'Telekommunikations-Enterprise-Sicherheit',
    hero_title_1: 'Sichern Sie Ihr',
    hero_title_2: 'Digitales Imperium',
    hero_desc: 'Vollständiger Schutz für Telefone, Tablets, IoT-Geräte und Smart-City-Infrastruktur. Verteidigen Sie sich gegen Finanzbetrug, Identitätsdiebstahl und GSM-Angriffe.',
    features: 'Funktionen',
    solutions: 'Lösungen',
    security: 'Sicherheit',
    compliance: 'Compliance',
    about: 'Über uns',
    sign_in: 'Anmelden',
    start_trial: 'Kostenlos testen',
    watch_demo: 'Demo ansehen',
    trusted_by: 'Von 500+ Unternehmen vertraut',
    mobile_security: 'Mobile Sicherheit',
    financial_fraud: 'Finanzbetrug',
    identity_protection: 'Identitätsschutz',
    iot_smart_city: 'IoT/Smart City',
    gsm_telecom: 'GSM/Telekom',
    enterprise: 'Unternehmen',
    ai_security: 'KI-Sicherheit',
    threats_blocked: 'Blockierte Bedrohungen',
    devices_protected: 'Geschützte Geräte',
    detection_rate: 'Erkennungsrate',
    response_time: 'Reaktionszeit',
    select_region: 'Region & Sprache wählen',
    pricing_after_login: 'Preise nach Anmeldung verfügbar',
  },
};

export const useRegion = () => {
  const ctx = useContext(RegionContext);
  return ctx || { 
    region: REGIONS[0], 
    setRegion: () => {}, 
    t: (key) => TRANSLATIONS.en[key] || key,
    formatPrice: (usd) => `$${usd}`,
    language: 'en'
  };
};

export const RegionProvider = ({ children }) => {
  const [region, setRegionState] = useState(() => {
    const saved = sessionStorage.getItem('securesphere_region');
    if (saved) {
      const found = REGIONS.find(r => r.code === saved);
      if (found) return found;
    }
    return REGIONS[0]; // Default to US
  });

  const [language, setLanguage] = useState(region.language);

  const setRegion = useCallback((newRegion) => {
    setRegionState(newRegion);
    setLanguage(newRegion.language);
    sessionStorage.setItem('securesphere_region', newRegion.code);
  }, []);

  // Translation function
  const t = useCallback((key) => {
    const translations = TRANSLATIONS[language] || TRANSLATIONS.en;
    return translations[key] || TRANSLATIONS.en[key] || key;
  }, [language]);

  // Format price in local currency
  const formatPrice = useCallback((usdAmount) => {
    if (usdAmount === 'Custom') return region.language === 'en' ? 'Custom' : t('custom') || 'Custom';
    
    const rate = EXCHANGE_RATES[region.currency] || 1;
    const localAmount = Math.round(usdAmount * rate);
    
    // Format based on currency
    if (region.currency === 'JPY') {
      return `${region.symbol}${localAmount.toLocaleString()}`;
    }
    return `${region.symbol}${localAmount.toLocaleString()}`;
  }, [region, t]);

  // Get pricing in local currency
  const getPricing = useCallback(() => {
    return {
      starter: {
        price: formatPrice(2499),
        period: '/month'
      },
      enterprise: {
        price: formatPrice(7999),
        period: '/month'
      },
      carrier: {
        price: 'Custom',
        period: ''
      }
    };
  }, [formatPrice]);

  return (
    <RegionContext.Provider value={{ 
      region, 
      setRegion, 
      t, 
      formatPrice, 
      getPricing,
      language,
      regions: REGIONS 
    }}>
      {children}
    </RegionContext.Provider>
  );
};

export default RegionContext;
